import { useContext, useMemo } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReportCard from "components/ReportCard";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { DisplayInOptionsEnum, SummaryOption } from "constants/constants";
import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { IMillReportCardProps } from "pages/Corporate/Locations/MillDashboards/MillDashboardInterfaces";
import { IDowntimeTotalsSummarized } from "pages/Corporate/Locations/MillDashboards/SummaryReportDashboard/components/TotalDowntimeByMachineCenter/TotalDowntimeByMachineCenterInterfaces";
import { percentageChecker } from "utilities/utilities";
import { mapCorporateFiltersToMillFilters } from "../../../utilities/mapCorporateFiltersToMillFilters";
import { useStyles } from "./MachineCentersPopover.styles";

const MachineCentersPopover = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();

    const {
        filters,
        selectedFailureCode,
        selectedSecondaryFailureCode,
        selectedFailureType,
        selectedMillPopoverData,
        handleSetSelectedMillPopoverData,
        totalDowntimeSeconds,
    } = useContext(CorporateDashboardContext);

    const millFilters = useMemo(() => mapCorporateFiltersToMillFilters(filters), [filters]);
    const displayIn = filters.displayIn;

    const { downtimeSummaryPerMachineCenter, loading } =
        MillsEntity.GetTotalDowntimeSummaryByCodePerMachineCenterMillDashboard(
            millFilters,
            SummaryOption.BY_SECONDARY_CODE,
            Number(selectedMillPopoverData?.millId),
            Number(selectedFailureCode?.id),
            Number(selectedSecondaryFailureCode?.id),
        );

    const downtimeSummaryFormatted = useMemo(
        () =>
            downtimeSummaryPerMachineCenter.reduce((acc: IDowntimeTotalsSummarized[], obj) => {
                const machineCenterId = obj.machine_center.machine_center_id;
                const existingMachineCenterIndex = acc.findIndex(
                    (summary) => summary.machineCenterId === machineCenterId,
                );

                if (!(existingMachineCenterIndex >= 0)) {
                    acc = [
                        ...acc,
                        {
                            machineCenterId: obj.machine_center.machine_center_id,
                            name: obj.machine_center.short_name || obj.machine_center.name,
                            typeTotals: [
                                {
                                    name: obj.failure_data.name,
                                    color_code: selectedFailureType?.color_code || "",
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                },
                            ],
                        },
                    ];
                } else {
                    const existingFailureTypeIndex = acc[existingMachineCenterIndex].typeTotals.findIndex(
                        (summary) => summary.name === obj.failure_data.name,
                    );

                    if (!(existingFailureTypeIndex >= 0)) {
                        acc[existingMachineCenterIndex] = {
                            ...acc[existingMachineCenterIndex],
                            typeTotals: [
                                ...acc[existingMachineCenterIndex].typeTotals,
                                {
                                    name: obj.failure_data.name,
                                    color_code: selectedFailureType?.color_code || "",
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                },
                            ],
                        };
                    } else {
                        acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex] = {
                            ...acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex],
                            total_seconds:
                                acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex].total_seconds +
                                obj.total_seconds,
                            total_occurrences:
                                acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex].total_occurrences +
                                obj.total_occurrences,
                        };
                    }
                }
                return acc;
            }, []),
        [downtimeSummaryPerMachineCenter, selectedFailureType?.color_code],
    );

    const graphData = useMemo(
        () =>
            downtimeSummaryFormatted
                .map((downtimeSummary) => {
                    let data: { [key: string]: string | number } = {
                        machineCenterId: downtimeSummary.machineCenterId,
                        machineCenter: downtimeSummary.name,
                        countryCode: countryCode as string,
                    };
                    downtimeSummary.typeTotals.forEach((total) => {
                        const secondsToMinutes =
                            Math.round(total.total_seconds / 60) < 1 ? 1 : Math.round(total.total_seconds / 60);
                        data = {
                            ...data,
                            [`${total.name}`]:
                                displayIn === DisplayInOptionsEnum.MINUTES
                                    ? secondsToMinutes
                                    : displayIn === DisplayInOptionsEnum.PERCENT
                                    ? totalDowntimeSeconds
                                        ? (100 * total.total_seconds) / totalDowntimeSeconds
                                        : 0
                                    : total.total_occurrences,
                            [`${total.name}Color`]: total.color_code,
                        };
                    });

                    const totalDowntime = downtimeSummary.typeTotals.reduce(
                        (acc, obj) =>
                            displayIn === DisplayInOptionsEnum.MINUTES
                                ? (Math.round(obj.total_seconds / 60) < 1 ? 1 : Math.round(obj.total_seconds / 60)) +
                                  acc
                                : displayIn === DisplayInOptionsEnum.PERCENT
                                ? (totalDowntimeSeconds ? (100 * obj.total_seconds) / totalDowntimeSeconds : 0) + acc
                                : obj.total_occurrences + acc,
                        0,
                    );
                    data = {
                        ...data,
                        totalDowntimeByMill:
                            displayIn === DisplayInOptionsEnum.PERCENT
                                ? percentageChecker(totalDowntime)
                                : totalDowntime,
                    };

                    return data;
                })
                .sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill))
                .slice(-5),
        [downtimeSummaryFormatted, totalDowntimeSeconds, displayIn, countryCode],
    );

    return (
        <ReportCard
            showNoData={graphData?.length === 0}
            title={selectedMillPopoverData?.title || ""}
            className={classes.mainCardReportWrapper}
            headerActions={
                <IconButton
                    size="small"
                    aria-label="close"
                    onClick={() => handleSetSelectedMillPopoverData(null)}
                    className={classes.closeButton}
                >
                    <CloseIcon className={classes.closeButtonIcon} />
                </IconButton>
            }
        >
            {loading ? (
                <div className={classes.noData}>
                    <CircularProgress size={24} color="info" />
                </div>
            ) : (
                <div className={classes.graphWrapper}>
                    <ReportDowntimeBarChart
                        data={graphData}
                        noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
                        index="machineCenter"
                        noTrend
                        noLegend
                    />
                </div>
            )}
        </ReportCard>
    );
};

export default MachineCentersPopover;
